import React from "react";
import { useQuery } from "react-query";
import { 
  Box, Typography, Paper, CircularProgress, 
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton
} from "@mui/material";
import moment from "moment";
import { getEmployeeCapacityReport } from "../../api/services/Candidate/capacityReport";
import { QK } from "../../base/qk";

const headerStyle = {
  fontWeight: 800,
  fontSize: "12px",
  color: "#334155",
  py: 1.5,
  borderRight: "1px solid #cbd5e1",
  borderBottom: "2px solid #cbd5e1",
  whiteSpace: 'nowrap'
};

const cellStyle = {
  fontSize: "12px",
  color: "#475569",
  borderRight: "1px solid #e2e8f0",
  py: 1
};

const EmployeeCapacityReport = ({ selectedDate, setSelectedDate, locationId, areaId }) => {
  const days = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

  const { data: reportResponse, isLoading } = useQuery(
    [QK.capacityReport, selectedDate, locationId, areaId],
    () => getEmployeeCapacityReport(
      moment(selectedDate).format("YYYY-MM-DD"), 
      locationId, 
      areaId
    ),
    { enabled: !!selectedDate, refetchOnWindowFocus: false }
  );

  const reportData = reportResponse?.data || [];

  const handlePreviousWeek = () => {
    setSelectedDate(moment(selectedDate).subtract(1, 'week').toDate());
  };

  const handleNextWeek = () => {
    setSelectedDate(moment(selectedDate).add(1, 'week').toDate());
  };

  if (isLoading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
      <CircularProgress size={30} thickness={4} sx={{ color: '#3b82f6' }} />
    </Box>
  );

  return (
    <Box sx={{ p: 2, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 700, color: "#334155", display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box 
            component="span" 
            onClick={handlePreviousWeek}
            sx={{ color: "#3b82f6", cursor: 'pointer', userSelect: 'none', px: 1, '&:hover': { opacity: 0.7 } }}
          >
            {"<<"}
          </Box>
          
          {moment(selectedDate).startOf('isoWeek').format("D MMM YYYY")} – {moment(selectedDate).endOf('isoWeek').format("D MMM YYYY")}
          
          <Box 
            component="span" 
            onClick={handleNextWeek}
            sx={{ color: "#3b82f6", cursor: 'pointer', userSelect: 'none', px: 1, '&:hover': { opacity: 0.7 } }}
          >
            {">>"}
          </Box>
        </Typography>
      </Box>

      <TableContainer component={Paper} elevation={0} sx={{ border: "1px solid #cbd5e1", borderRadius: "4px" }}>
        <Table sx={{ minWidth: 1200 }} size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "#f1f5f9" }}>
              <TableCell sx={headerStyle}>Employee Name</TableCell>
              <TableCell align="center" sx={headerStyle}>Contracted Hours</TableCell>
              <TableCell align="center" sx={headerStyle}>Maximum Hours</TableCell>
              <TableCell align="center" sx={headerStyle}>Weekly Hours</TableCell>
              {days.map((day) => (
                <TableCell key={day} align="center" sx={headerStyle}>{day}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {reportData.length > 0 ? (
              reportData.map((row, index) => (
                <TableRow 
                  key={index} 
                  sx={{ 
                    '&:nth-of-type(odd)': { bgcolor: '#fff' },
                    '&:nth-of-type(even)': { bgcolor: '#f8fafc' },
                    '&:hover': { bgcolor: '#f1f5f9' },
                    borderBottom: "1px solid #e2e8f0"
                  }}
                >
                  <TableCell sx={{ fontWeight: 500, color: "#334155", borderRight: "1px solid #e2e8f0" }}>
                    {row.resource_name}
                  </TableCell>
                  <TableCell align="center" sx={cellStyle}>{row.contracted_hours}</TableCell>
                  <TableCell align="center" sx={cellStyle}>{row.maximum_hours}</TableCell>
                  <TableCell align="center" sx={{ ...cellStyle, fontWeight: 700 }}>{row.weekly_hours}</TableCell>
                  <TableCell align="center" sx={cellStyle}>{row.Monday}</TableCell>
                  <TableCell align="center" sx={cellStyle}>{row.Tuesday}</TableCell>
                  <TableCell align="center" sx={cellStyle}>{row.Wednesday}</TableCell>
                  <TableCell align="center" sx={cellStyle}>{row.Thursday}</TableCell>
                  <TableCell align="center" sx={cellStyle}>{row.Friday}</TableCell>
                  <TableCell align="center" sx={cellStyle}>{row.Saturday}</TableCell>
                  <TableCell align="center" sx={cellStyle}>{row.Sunday}</TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 3, color: "#64748b" }}>
                  No data available for the selected criteria.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default EmployeeCapacityReport;